/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae summary component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * React
 */
import * as React from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { faUser } from '@fortawesome/pro-duotone-svg-icons/faUser';

import { faHeartbeat } from '@fortawesome/pro-duotone-svg-icons/faHeartbeat';

import { faBuilding } from '@fortawesome/pro-duotone-svg-icons/faBuilding';

import Grid from '@material-ui/core/Grid';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/Neon.Dtos';
import InformationCard from '../InformationCard/InformationCard';
import { InstitutionContext } from '@ngt/online-patient-management-ui';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeInformationProps {
    sae?: Dtos.Sae<Dtos.SaeForm, Dtos.Patient> | null;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    item: {
        display: 'flex',

        '& > *': {
            flex: '1 1 auto'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const getSaeStatus = (status?: Dtos.SaeStatus) => {
    switch (status) {
        case Dtos.SaeStatus.Added: return 'New';
        case Dtos.SaeStatus.New: return 'New';
        case Dtos.SaeStatus.AwaitingForReview: return 'Awaiting review';
        case Dtos.SaeStatus.Approved: return 'Approved';
        case Dtos.SaeStatus.Rejected: return 'Rejected';
        case Dtos.SaeStatus.Incorrect: return 'Incorrectly Identified';
    }

    return;
}

const SaeInformation: React.FunctionComponent<ISaeInformationProps> = ({
    sae
}) => {

    const theme = useTheme()
    const classes = useStyles();
    const [institution] = React.useContext(InstitutionContext);

    return (
        <Grid
            container
            spacing={3}
        >
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.primary.main}
                    icon={faUser}
                    title="Patient"
                    text={sae?.patient.studyNumber}
                    caption={sae?.patient.initials}
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color={theme.palette.secondary.main}
                    icon={faBuilding}
                    title="Institution"
                    text={institution?.name}
                    caption=""
                />
            </Grid>
            <Grid
                item
                xs={12}
                md={4}
                className={classes.item}
            >
                <InformationCard
                    color="#76c6d7"
                    icon={faHeartbeat}
                    title="SAE number"
                    text={sae ? `${sae?.form.saeNumber} (${sae?.form.followUpNumber === 0 ? 'Initial' : 'Follow Up No.' + sae?.form.followUpNumber})` : ''}
                    caption={sae ? getSaeStatus(sae?.status) : ''}
                />
            </Grid>
        </Grid>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default SaeInformation;
